import { useMemo } from 'react';
import {
  navigationMenuSections,
  type MenuVisibility,
  type NavigationMenuItem,
  type NavigationMenuSection,
} from '../config/navigationMenu';

export interface UseNavigationMenuOptions {
  showBrandDashboards: boolean;
  isAdmin: boolean;
  /** Demo host: show admin-only menu items (e.g. product-doc) */
  isDemo?: boolean;
}

function isVisibilityAllowed(
  visibility: MenuVisibility | undefined,
  options: UseNavigationMenuOptions,
): boolean {
  if (!visibility || visibility === 'always') return true;
  if (visibility === 'multiBranch') return options.showBrandDashboards;
  if (visibility === 'admin') return options.isAdmin || Boolean(options.isDemo);
  return true;
}

export function useNavigationMenu(options: UseNavigationMenuOptions) {
  const visibleSections = useMemo((): NavigationMenuSection[] => {
    return navigationMenuSections
      .filter((section) => isVisibilityAllowed(section.visibility, options))
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => isVisibilityAllowed(item.visibility, options)),
      }))
      .filter((section) => section.items.length > 0);
  }, [options.showBrandDashboards, options.isAdmin, options.isDemo]);

  const visibleDashboardIds = useMemo(() => {
    const ids = new Set<string>();
    visibleSections.forEach((section) => {
      section.items.forEach((item) => ids.add(item.id));
    });
    return ids;
  }, [visibleSections]);

  const dashboardLookup = useMemo(() => {
    const map = new Map<
      string,
      { item: NavigationMenuItem; sectionTitle: string }
    >();
    visibleSections.forEach((section) => {
      section.items.forEach((item) => {
        map.set(item.id, { item, sectionTitle: section.title });
      });
    });
    return map;
  }, [visibleSections]);

  return { visibleSections, visibleDashboardIds, dashboardLookup };
}
